/**
 * @file ConversationRow.tsx
 * @description Строка диалога в списке окна «Сообщения»
 * (chat-window-design.md §2): 72 от 768, 76 на телефоне.
 *
 *   ● Имя клиента                           14:32
 *     Вы: последнее сообщение…               (2)
 *     Заказ Z-2026-000006 · [Передан вам]
 *
 * Что режется первым — по ширине строки (контейнерные запросы, а не
 * расчёт в JS): текст сообщения → имя ведущего сокращается до «А. Кравец»,
 * потом пропадает → номер заказа сжимается до «Z-…000006» → имя клиента
 * (не уже 96). Время, счётчик и пилюля не режутся.
 *
 * Строка в `memo`: ввод в поиске или в поле ответа перерисовывает список,
 * но не строки — у них стабильные пропсы (вид строки из `useMemo` списка,
 * `onSelect(id)` — один на весь список).
 */

import { memo, type KeyboardEvent } from "react";

import { Badge } from "../ui/badge";
import { cn } from "@/lib/utils";
import type { ConversationRowView, RowPillTone } from "./chat-row-view";
import { CHAT_TEXT } from "./chat-texts";

const PILL_VARIANT: Record<RowPillTone, "info" | "neutral" | "warning"> = {
  info: "info",
  neutral: "neutral",
  warning: "warning",
};

interface Props {
  id: string;
  view: ConversationRowView;
  selected: boolean;
  /** Новый диалог — подсветка гаснет за 2 с. */
  fresh: boolean;
  onSelect: (id: string) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
}

export const ConversationRow = memo(function ConversationRow({ id, view, selected, fresh, onSelect, onKeyDown }: Props) {
  const marker = view.marker;
  return (
    <button
      type="button"
      data-chat-row=""
      aria-current={selected ? "true" : undefined}
      onClick={() => onSelect(id)}
      onKeyDown={onKeyDown}
      className={cn(
        "@container relative flex min-h-[76px] w-full flex-col justify-center gap-0.5 py-2 pl-4 pr-3 text-left outline-none md:min-h-[72px]",
        "transition-colors duration-700 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        selected ? "bg-accent" : fresh ? "bg-info/12" : "hover:bg-surface-low",
      )}
    >
      {selected ? <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-primary" /> : null}

      <span className="flex min-w-0 items-center gap-2">
        <span aria-hidden className={cn("size-2 shrink-0 rounded-full", view.unread ? "bg-primary" : "bg-transparent")} />
        <span className={cn("min-w-24 flex-1 truncate text-sm text-on-surface", view.unread || marker?.kind === "pill" ? "font-semibold" : "font-medium")}>
          {view.name}
        </span>
        <span className="shrink-0 text-xs tabular-nums text-on-surface-variant">{view.time}</span>
      </span>

      <span className="flex min-w-0 items-center gap-2 pl-4">
        <span className={cn("min-w-0 flex-1 truncate text-sm", view.previewIsEvent ? "text-on-surface-muted" : "text-on-surface-variant")}>
          {view.previewAuthor ? <span className="text-on-surface">{view.previewAuthor}</span> : null}
          {view.preview}
        </span>
        {view.unreadCount > 0 ? (
          <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-semibold tabular-nums text-on-primary">
            <span className="sr-only">{CHAT_TEXT.unreadAria(view.unreadCount)}: </span>
            {view.unreadCount}
          </span>
        ) : null}
      </span>

      <span className="flex min-w-0 items-center gap-1.5 pl-4 text-xs text-on-surface-variant">
        <span className="hidden min-w-0 truncate @min-[340px]:inline">{view.context}</span>
        <span className="min-w-0 truncate @min-[340px]:hidden">{view.contextShort}</span>
        {marker?.kind === "pill" ? (
          <Badge variant={PILL_VARIANT[marker.tone]} className="shrink-0">
            {marker.text}
          </Badge>
        ) : null}
        {marker?.kind === "leads" ? (
          <span className={cn("hidden min-w-0 truncate @min-[240px]:inline", marker.mine && "text-on-surface")}>
            <span aria-hidden>· </span>
            <span className="hidden @min-[300px]:inline">{marker.full}</span>
            <span className="@min-[300px]:hidden">{marker.short}</span>
          </span>
        ) : null}
      </span>
    </button>
  );
});
